import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Sparkles, Network, Search, Shuffle } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import SearchHeader from "@/components/search-header";
import Footer from "@/components/footer";
import {
  loadAllArtworks,
  loadArtworkById,
  getRandomArtwork,
  searchArtworks,
  StaticArtwork,
} from "@/lib/static-data";
import { getPreloadedData } from "@/lib/hydration";
import { trackEvent } from "@/lib/analytics";

const SITE_URL = "https://ariadnesgallery.com";

const EMOTION_THEMES = [
  { slug: "eerie",     label: "Creepy",     description: "Eerie and haunting art for Halloween",    gradient: "from-gray-800 to-purple-900",     emoji: "🕯️" },
  { slug: "wonder",    label: "Wonder",     description: "Art that leaves you speechless",          gradient: "from-indigo-500 to-purple-600",   emoji: "✨" },
  { slug: "joy",       label: "Joy",        description: "The most uplifting paintings in history", gradient: "from-yellow-400 to-orange-500",   emoji: "☀️" },
  { slug: "sadness",   label: "Melancholy", description: "Art of grief, loss, and longing",         gradient: "from-blue-500 to-slate-600",      emoji: "🌧️" },
  { slug: "nostalgia", label: "Nostalgia",  description: "Art aching with the passage of time",     gradient: "from-amber-500 to-orange-600",    emoji: "🕰️" },
  { slug: "tension",   label: "Tension",    description: "Art that keeps you on edge",              gradient: "from-red-500 to-rose-700",        emoji: "⚡" },
  { slug: "arousal",   label: "Energy",     description: "The most intense and electric artworks",  gradient: "from-orange-500 to-red-600",      emoji: "🔥" },
  { slug: "valence",   label: "Beauty",     description: "The most uplifting and serene art",       gradient: "from-emerald-400 to-teal-600",    emoji: "🌿" },
  { slug: "anger",     label: "Defiance",   description: "Art of rebellion and confrontation",      gradient: "from-red-700 to-red-900",         emoji: "⚔️" },
  { slug: "dominance", label: "Dominance",  description: "Art that overwhelms and commands",        gradient: "from-slate-600 to-gray-900",      emoji: "🏛️" },
  { slug: "interest",  label: "Curiosity",  description: "The most intellectually rich artworks",   gradient: "from-cyan-500 to-blue-600",       emoji: "🔍" },
];

interface FeaturedArtwork {
  id: number;
  title: string;
  artist: string;
  imageUrl: string;
  year?: string | number;
}

export default function Landing() {
  const [, setLocation] = useLocation();
  const [comprehensiveArtworks, setComprehensiveArtworks] = useState<StaticArtwork[]>([]);
  const [searchResults, setSearchResults] = useState<StaticArtwork[]>([]);
  const [featured, setFeatured] = useState<FeaturedArtwork[]>([]);

  useEffect(() => {
    loadAllArtworks().then(setComprehensiveArtworks).catch(console.error);
  }, []);

  useEffect(() => {
    async function loadFeatured() {
      const preloaded = getPreloadedData() as { featuredIds?: number[] } | null;
      const ids = preloaded?.featuredIds ?? [];
      if (ids.length === 0) return;
      const resolved = await Promise.all(
        ids.map(async (id) => {
          const a = await loadArtworkById(id);
          if (!a || !a.imageUrl) return null;
          return {
            id: a.id,
            title: a.title,
            artist: a.artist ?? "Unknown",
            imageUrl: a.imageUrl,
            year: a.date ?? a.year,
          } as FeaturedArtwork;
        }),
      );
      setFeatured(resolved.filter((x): x is FeaturedArtwork => x !== null));
    }
    loadFeatured();
  }, []);

  const handleArtworkSelect = (artworkId: number) => {
    setLocation(`/artwork/${artworkId}/`);
    trackEvent("view_artwork", "navigation", "landing_search", artworkId);
  };

  const handleSearch = (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    setSearchResults(searchArtworks(comprehensiveArtworks, query, 20));
  };

  const handleRandomArtwork = () => {
    const art = getRandomArtwork(comprehensiveArtworks);
    if (art?.id) {
      trackEvent("view_artwork", "navigation", "landing_random", art.id);
      setLocation(`/artwork/${art.id}/`);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Ariadne's Gallery — Explore 116,000 artworks through emotion and connection"
        description="Browse 116,000 paintings scored across 10 emotional dimensions and connected through an interactive knowledge graph. Discover masterpieces by feeling, not just by name."
        url={`${SITE_URL}/`}
        type="website"
      />

      <SearchHeader
        artworks={searchResults}
        onArtworkSelect={handleArtworkSelect}
        onSearch={handleSearch}
        totalCount={comprehensiveArtworks.length}
        onRandomArtwork={handleRandomArtwork}
      />

      <main className="pt-16">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-b from-indigo-50 via-white to-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
            <h1 className="text-5xl sm:text-6xl font-bold tracking-tight mb-6">
              Ariadne's Gallery
            </h1>
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-2xl mx-auto mb-10">
              Explore 116,000 artworks through emotion and connection.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                href="/emotion"
                className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full text-base font-medium transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                Browse by emotion
              </Link>
              <Link
                href="/emotion-search"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 rounded-full text-base font-medium transition-colors"
              >
                <Search className="w-4 h-4" />
                Search by emotional profile
              </Link>
              <button
                onClick={handleRandomArtwork}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 rounded-full text-base font-medium transition-colors"
              >
                <Shuffle className="w-4 h-4" />
                Surprise me
              </button>
            </div>
          </div>
        </section>

        {/* Featured artworks */}
        {featured.length > 0 && (
          <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="flex items-baseline justify-between mb-8">
              <h2 className="text-3xl font-semibold">Featured masterpieces</h2>
              <Link href="/emotion" className="text-sm text-indigo-600 hover:underline">
                Browse the full collection →
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {featured.map((art) => (
                <Link
                  key={art.id}
                  href={`/artwork/${art.id}/`}
                  className="group block"
                  onClick={() => trackEvent("view_artwork", "navigation", "landing_featured", art.id)}
                >
                  <div className="aspect-square overflow-hidden rounded-lg bg-gray-100">
                    <img
                      src={art.imageUrl}
                      alt={`${art.title} by ${art.artist}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="mt-3 font-medium line-clamp-1 group-hover:text-indigo-600 transition-colors">
                    {art.title}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-1">
                    {art.artist}
                    {art.year ? `, ${art.year}` : ""}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* How it works */}
        <section className="bg-gray-50 border-y">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="text-3xl font-semibold mb-3 text-center">How it works</h2>
            <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-12">
              Every artwork is scored across ten emotional dimensions and linked to the works that
              feel most like it. The gallery turns those scores into things you can explore.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl border p-6">
                <Sparkles className="w-8 h-8 text-indigo-600 mb-4" />
                <h3 className="font-semibold text-lg mb-2">Emotion analysis</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Each artwork has a fingerprint across Valence, Arousal, Dominance, Joy, Sadness,
                  Wonder, and four more emotional axes.
                </p>
                <Link href="/emotion-search" className="text-sm text-indigo-600 hover:underline">
                  Try emotion search →
                </Link>
              </div>
              <div className="bg-white rounded-xl border p-6">
                <Network className="w-8 h-8 text-indigo-600 mb-4" />
                <h3 className="font-semibold text-lg mb-2">Knowledge graph</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Artworks with similar emotional profiles are linked into a network you can
                  navigate visually, jumping between artists, movements, and centuries.
                </p>
                <Link href="/about" className="text-sm text-indigo-600 hover:underline">
                  How it's built →
                </Link>
              </div>
              <div className="bg-white rounded-xl border p-6">
                <Search className="w-8 h-8 text-indigo-600 mb-4" />
                <h3 className="font-semibold text-lg mb-2">Discovery</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Search by title or artist, dial in an emotional profile, or open a curated
                  collection — the gallery surfaces work you wouldn't find by name.
                </p>
                <Link href="/emotion" className="text-sm text-indigo-600 hover:underline">
                  Browse emotion collections →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Emotion entry grid */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold mb-3">Browse by feeling</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Ten curated collections, each ranking the most expressive works on a single
              emotional dimension.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {EMOTION_THEMES.map((theme) => (
              <Link
                key={theme.slug}
                href={`/emotion/${theme.slug}`}
                className="group block rounded-xl overflow-hidden border hover:shadow-md transition-shadow"
                onClick={() => trackEvent("click", "navigation", `landing_emotion_${theme.slug}`)}
              >
                <div className={`bg-gradient-to-r ${theme.gradient} px-5 py-4 flex items-center gap-3`}>
                  <span className="text-2xl">{theme.emoji}</span>
                  <div className="min-w-0">
                    <div className="text-white font-bold">{theme.label}</div>
                    <div className="text-white/75 text-xs truncate">{theme.description}</div>
                  </div>
                  <div className="ml-auto text-white/60 group-hover:text-white transition-colors">
                    →
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
